import React, { useContext, useState } from "react";
import { partition } from "lodash";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { SearchResultsContext } from "../Maps/SearchResultsContext";
import { displayForResult } from "./utils";

const OmniboxResultItem = ({ result }: { result: OmniboxSearchResult }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { title, description, iconName, type } = displayForResult({ result });

  return (
    <SelectionCard.Item
      key={result.id}
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={result}
    >
      <FlexRow style={{ gap: spacing.m.value }}>
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        <div style={{ opacity: isHovered ? 1 : 0 }}>
          {/* TODO: check on tag height */}
          <Tag styleVariant="default">{type}</Tag>
        </div>
      </FlexRow>
    </SelectionCard.Item>
  );
};

export const OmniboxResults = ({
  handleSearchResultClick,
}: {
  handleSearchResultClick: (searchResult: OmniboxSearchResult) => void;
}) => {
  const { searchResultsState } = useContext(SearchResultsContext);

  if (searchResultsState.loading) {
    return <div>Loading...</div>;
  } else if (!searchResultsState.results?.length) {
    return (
      <EmptyState
        icon="missing-location"
        paddingTop={spacing["2xl"].value}
        title="No results found"
        body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
      />
    );
  }

  const [strictMatches, fuzzyMatches] = partition(
    searchResultsState.results,
    result => result.strictMatch,
  );

  return (
    <SelectionCard.Group
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {strictMatches.length > 0 && (
        <Body type="emphasis" size="small">
          {searchResultsState.isTextSearch ? "Exact Matches" : "Nearby Results"}
        </Body>
      )}
      {strictMatches.map(result => (
        <OmniboxResultItem result={result} key={result.id} />
      ))}
      {fuzzyMatches.length > 0 && (
        <>
          <Body
            type="emphasis"
            size="small"
            style={{
              paddingTop: strictMatches.length > 0 ? spacing.m.value : 0,
            }}
          >
            Related Results
          </Body>
          {fuzzyMatches.map(result => (
            <OmniboxResultItem result={result} key={result.id} />
          ))}
        </>
      )}
    </SelectionCard.Group>
  );
};
